import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Popover } from '@mui/material'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import dayjs, { Dayjs } from 'dayjs'
import { colors } from '../../const/colors'

export const CALENDAR_SX = {
  '& .MuiPickersCalendarHeader-label': {
    fontSize: '15px',
    fontWeight: 800,
    color: colors.navy,
    letterSpacing: '-0.01em',
  },
  '& .MuiPickersArrowSwitcher-button': {
    color: colors.navy,
    borderRadius: '9px',
    '&:hover': { background: colors.bg },
  },
  '& .MuiDayCalendar-weekDayLabel': {
    fontSize: '12px',
    fontWeight: 700,
    color: colors.slate,
  },
  '& .MuiPickersDay-root': {
    fontSize: '13.5px',
    fontWeight: 600,
    borderRadius: '10px',
    color: colors.textBody,
    '&:hover': { background: colors.rowHover },
    '&.MuiPickersDay-today': {
      border: `1.5px solid ${colors.border3}`,
      fontWeight: 800,
      background: 'transparent',
    },
    '&.Mui-selected': {
      background: `${colors.navy} !important`,
      color: '#fff',
      fontWeight: 800,
    },
    '&.Mui-selected:hover': {
      background: `${colors.navyDark} !important`,
    },
  },
  '& .MuiDayCalendar-monthContainer': {
    minHeight: '220px',
  },
} as const

const formatInputDate = (inputVal: string): string => {
  if (!inputVal) return ''
  const [year, month, day] = inputVal.split('-')
  return `${day}.${month}.${year}`
}

interface DateFilterPillProps {
  label: string
  /** ISO date string 'YYYY-MM-DD', or empty string when unset */
  value: string
  onChange: (val: string) => void
  e2e?: string
}

export function DateFilterPill({ label, value, onChange, e2e }: DateFilterPillProps) {
  const { t } = useTranslation('generic')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const dayjsValue = value ? dayjs(value) : null

  const handleSelect = (newValue: Dayjs | null) => {
    onChange(newValue ? newValue.format('YYYY-MM-DD') : '')
    setAnchorEl(null)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 48,
          borderRadius: colors.radiusPill,
          background: colors.surface,
          border: `1px solid ${value ? colors.navy : colors.border3}`,
          transition: 'border-color .15s',
          '&:hover': { borderColor: colors.slateLight },
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={event => setAnchorEl(event.currentTarget)}
          data-e2e={e2e}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            height: '100%',
            px: '18px',
            pr: value ? '10px' : '18px',
            fontSize: '14px',
            color: value ? colors.navy : colors.textSecondary,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            userSelect: 'none',
            flex: 1,
          }}
        >
          {label}
          {value ? ` · ${formatInputDate(value)}` : ''}
        </Box>

        {value && (
          <Box
            onClick={() => onChange('')}
            data-e2e={e2e && `${e2e}-clear`}
            title={t('clear_date')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              background: colors.border,
              color: colors.textSecondary,
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: 1,
              cursor: 'pointer',
              userSelect: 'none',
              flexShrink: 0,
              mr: '12px',
              transition: 'background .12s, color .12s',
              '&:hover': { background: colors.red, color: '#fff' },
            }}
          >
            ×
          </Box>
        )}
      </Box>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            mt: '6px',
            borderRadius: '18px',
            boxShadow: '0 16px 40px -12px rgba(19,54,73,.28)',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
          },
        }}
      >
        <StaticDatePicker
          value={dayjsValue}
          onChange={handleSelect}
          sx={CALENDAR_SX}
          slotProps={{
            toolbar: { hidden: true },
            actionBar: { actions: [] },
          }}
        />
      </Popover>
    </>
  )
}
